function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import { useCallback, useEffect, useRef, useState } from 'react'; // @TODO:UPDATE

var RecorderErrors;

(function (RecorderErrors) {
  RecorderErrors["AbortError"] = "media_aborted";
  RecorderErrors["NotAllowedError"] = "permission_denied";
  RecorderErrors["NotFoundError"] = "no_specified_media_found";
  RecorderErrors["NotReadableError"] = "media_in_use";
  RecorderErrors["OverconstrainedError"] = "invalid_media_constraints";
  RecorderErrors["TypeError"] = "no_constraints";
  RecorderErrors["NONE"] = "";
  RecorderErrors["NO_RECORDER"] = "recorder_error";
})(RecorderErrors || (RecorderErrors = {}));

export default (({
  render,
  audio = true,
  video = false,
  onStop = () => null,
  blobPropertyBag,
  screen = false,
  mediaRecorderOptions = null,
  getStream
}) => {
  const mediaRecorder = useRef(null);
  const mediaChunks = useRef([]);
  const mediaStream = useRef(null);
  const [status, setStatus] = useState('idle');
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [mediaBlobUrl, setMediaBlobUrl] = useState(null);
  const [error, setError] = useState('NONE');
  const getMediaStream = useCallback(async () => {
    setStatus('acquiring_media');
    const requiredMedia = {
      audio: typeof audio === 'boolean' ? !!audio : audio,
      video: typeof video === 'boolean' ? !!video : video
    };

    try {
      if (getStream) {
        mediaStream.current = getStream();
      } else if (screen) {
        // @ts-ignore
        const stream = await window.navigator.mediaDevices.getDisplayMedia({
          video: video || true
        });

        if (audio) {
          const audioStream = await window.navigator.mediaDevices.getUserMedia({
            audio
          });
          audioStream.getAudioTracks().forEach(audioTrack => stream.addTrack(audioTrack));
        }

        mediaStream.current = stream;
      } else {
        const stream = await window.navigator.mediaDevices.getUserMedia(requiredMedia);
        mediaStream.current = stream;
      }

      setStatus('idle');
    } catch (error) {
      setError(error.name);
      setStatus('idle');
    }
  }, [audio, video, screen]);
  useEffect(() => {
    if (!window.MediaRecorder) {
      throw new Error('Unsupported Browser');
    }

    if (screen) {
      if (!window.navigator.mediaDevices.getDisplayMedia) {
        throw new Error('This browser doesn\'t support screen capturing');
      }
    }

    const checkConstraints = mediaType => {
      const supportedMediaConstraints = navigator.mediaDevices.getSupportedConstraints();
      const unSupportedConstraints = Object.keys(mediaType).filter(constraint => !supportedMediaConstraints[constraint]);

      if (unSupportedConstraints.length > 0) {
        console.error(`The constraints ${unSupportedConstraints.join(',')} doesn't support on this browser. Please check your ReactMediaRecorder component.`);
      }
    };

    if (typeof audio === 'object') {
      checkConstraints(audio);
    }

    if (typeof video === 'object') {
      checkConstraints(video);
    }

    if (mediaRecorderOptions && mediaRecorderOptions.mimeType) {
      if (!window.MediaRecorder.isTypeSupported(mediaRecorderOptions.mimeType)) {
        console.error('The specified MIME type you supplied for MediaRecorder doesn\'t support this browser');
      }
    }

    if (!mediaStream.current) {
      getMediaStream();
    }
  }, [audio, screen, video, getMediaStream, mediaRecorderOptions]); // Media Recorder Handlers

  const startRecording = async () => {
    setError('NONE');

    if (!mediaStream.current) {
      await getMediaStream();
    }

    if (mediaStream.current) {
      const isStreamEnded = mediaStream.current.getTracks().some(track => track.readyState === 'ended');

      if (isStreamEnded) {
        await getMediaStream();
      }

      mediaRecorder.current = new window.MediaRecorder(mediaStream.current);
      mediaRecorder.current.ondataavailable = onRecordingActive;
      mediaRecorder.current.onstop = onRecordingStop;

      mediaRecorder.current.onerror = () => {
        setError('NO_RECORDER');
        setStatus('idle');
      };

      mediaRecorder.current.start();
      setStatus('recording');
    }
  };

  const onRecordingActive = ({
    data
  }) => {
    mediaChunks.current.push(data);
  };

  const onRecordingStop = () => {
    const [chunk] = mediaChunks.current;

    const blobProperty = _objectSpread({
      type: chunk.type
    }, blobPropertyBag || (video ? {
      type: 'video/mp4'
    } : {
      type: 'audio/wav'
    }));

    const blob = new Blob(mediaChunks.current, blobProperty);
    const url = URL.createObjectURL(blob);
    setStatus('stopped');
    setMediaBlobUrl(url);
    onStop(url, blob);
  };

  const muteAudio = mute => {
    setIsAudioMuted(mute);

    if (mediaStream.current) {
      mediaStream.current.getAudioTracks().forEach(audioTrack => {
        audioTrack.enabled = !mute;
      });
    }
  };

  const pauseRecording = () => {
    if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
      mediaRecorder.current.pause();
    }
  };

  const resumeRecording = () => {
    if (mediaRecorder.current && mediaRecorder.current.state === 'paused') {
      mediaRecorder.current.resume();
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current) {
      if (mediaRecorder.current.state !== 'inactive') {
        setStatus('stopping');
        mediaRecorder.current.stop();
        mediaStream.current && mediaStream.current.getTracks().forEach(track => track.stop());
        mediaChunks.current = [];
      }
    }
  };

  return render({
    error: RecorderErrors[error],
    muteAudio: () => muteAudio(true),
    unMuteAudio: () => muteAudio(false),
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    mediaBlobUrl,
    status,
    isAudioMuted,
    previewStream: mediaStream.current ? new MediaStream(mediaStream.current.getVideoTracks()) : null,
    clearBlobUrl: () => setMediaBlobUrl(null)
  });
});